import styled from "styled-components";
import {
  ButtonComp,
  TextInputComp,
  CardComp,
} from "../../../../components";
import { RiArrowDropDownLine } from "react-icons/ri";
import {
  useCreateCompanyRoleMutation,
  useGetPlanModulesCatalogQuery,
  useUpdateCompanyRoleMutation,
} from "../../../../store/admin/settings/roleSlice";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import PermissionListSkeletonLoader from "../../../../components/Common/PermissionListSkeletonLoader";

const validationSchema = Yup.object({
  name: Yup.string().required("Role name is required"),
  description: Yup.string(),
  can_view_dashboard: Yup.boolean(),
  modules: Yup.array()
    .of(
      Yup.object({
        module_id: Yup.number().required(),
        sub_module_ids: Yup.array().of(Yup.number()),
      })
    )
    .min(1, "Select at least one module"),
});

function RoleForm({ initialRole = null, roleId = null }) {
  const navigate = useNavigate();
  const [permissionSearch, setPermissionSearch] = useState("");
  const { data: catalog = [], isLoading: catalogLoading, error: catalogError } =
    useGetPlanModulesCatalogQuery();
  const [createRole, { isLoading: creating }] = useCreateCompanyRoleMutation();
  const [updateRole, { isLoading: updating }] = useUpdateCompanyRoleMutation();

  const filteredCatalog = useMemo(() => {
    const query = permissionSearch.trim().toLowerCase();
    if (!query) return catalog;

    return catalog
      .map((mod) => {
        const subModules = mod.subModules || mod.sub_modules || [];
        const parentMatch = mod.name?.toLowerCase().includes(query);
        const matchingSubs = subModules.filter((sub) =>
          sub.name?.toLowerCase().includes(query)
        );

        if (parentMatch) {
          return { ...mod, _expandOnSearch: true, _matchingSubs: subModules };
        }
        if (matchingSubs.length > 0) {
          return {
            ...mod,
            subModules: matchingSubs,
            sub_modules: matchingSubs,
            _expandOnSearch: true,
            _matchingSubs: matchingSubs,
          };
        }
        return null;
      })
      .filter(Boolean);
  }, [catalog, permissionSearch]);

  const initialValues = useMemo(() => {
    if (!initialRole) {
      return {
        name: "",
        description: "",
        can_view_dashboard: false,
        modules: [],
      };
    }
    return {
      name: initialRole.name || "",
      description: initialRole.description || "",
      can_view_dashboard: Boolean(initialRole.can_view_dashboard),
      modules: (initialRole.modules || []).map((m) => ({
        module_id: m.id,
        sub_module_ids: m.sub_modules || [],
      })),
    };
  }, [initialRole]);

  if (catalogError) {
    console.error(catalogError);
  }

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const payload = {
        name: values.name.trim(),
        description: values.description?.trim() || undefined,
        can_view_dashboard: Boolean(values.can_view_dashboard),
        modules: values.modules.map((m) => ({
          module_id: m.module_id,
          sub_module_ids: m.sub_module_ids || [],
        })),
      };

      if (roleId) {
        await updateRole({ roleId, ...payload }).unwrap();
        toast.success("Role updated");
      } else {
        await createRole(payload).unwrap();
        toast.success("Role created");
      }
      navigate("/super_admin/settings/role_management");
    } catch (err) {
      const message =
        err?.data?.message || err?.error || "Unable to save role";
      toast.error(typeof message === "string" ? message : "Unable to save role");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Wrapper>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, errors, touched, isSubmitting }) => (
          <Form>
            <CardComp
              cardClassName={"pt-4 mb-4"}
              cardBodyClassName={"py-0 row gx-5 gy-3 justify-content-between"}
            >
              <div className="col-12 col-lg-5">
                <TextInputComp
                  LabelText={"Role name"}
                  borderColor="#A1A0A0"
                  LabelClassName={"fw-bold"}
                  placeholder={"e.g. Loan Officer"}
                  name="name"
                />
                <div className="mt-3">
                  <TextInputComp
                    LabelText={"Description (optional)"}
                    borderColor="#A1A0A0"
                    LabelClassName={"fw-bold"}
                    placeholder={"What this role can do"}
                    name="description"
                  />
                </div>
                <div className="mt-3">
                  <label className="d-flex gap-2 align-items-start mb-0">
                    <input
                      type="checkbox"
                      name="can_view_dashboard"
                      checked={values.can_view_dashboard}
                      onChange={(e) =>
                        setFieldValue("can_view_dashboard", e.target.checked)
                      }
                      className="mt-1"
                    />
                    <span>
                      <span className="fw-bold d-block">
                        Organisation dashboard
                      </span>
                      <span className="text-muted small">
                        Allow this role to view the main dashboard with
                        organisation-wide summaries. Leave off for staff who
                        should only work inside their assigned modules.
                      </span>
                    </span>
                  </label>
                </div>
              </div>
              <div className="col-12 col-lg-6">
                <p className="fw-bold mb-2">Role permissions</p>
                <p className="text-muted small mb-3">
                  Choose modules available for your organisation.
                  Empty sub-module selection means no sub-modules for that
                  module.
                </p>
                {catalogLoading ? (
                  <PermissionListSkeletonLoader rows={8} />
                ) : (
                  <>
                    <div className="permission-search mb-3">
                      <input
                        type="search"
                        className="form-control"
                        placeholder="Search modules or permissions…"
                        value={permissionSearch}
                        onChange={(e) => setPermissionSearch(e.target.value)}
                        aria-label="Search role permissions"
                      />
                      {permissionSearch.trim() && (
                        <p className="text-muted small mt-2 mb-0">
                          {filteredCatalog.length} module
                          {filteredCatalog.length === 1 ? "" : "s"} found
                        </p>
                      )}
                    </div>
                    <div className="permissions">
                      {filteredCatalog.length === 0 ? (
                        <p className="text-muted small mb-0">
                          No permissions match &quot;{permissionSearch.trim()}&quot;
                        </p>
                      ) : (
                        filteredCatalog.map((mod) => (
                          <ModulePermissionBlock
                            key={mod.id}
                            module={mod}
                            selected={values.modules}
                            onChange={(next) => setFieldValue("modules", next)}
                            defaultOpen={Boolean(mod._expandOnSearch)}
                          />
                        ))
                      )}
                    </div>
                  </>
                )}
                {touched.modules && errors.modules && (
                  <p className="text-danger small mt-2">
                    {typeof errors.modules === "string"
                      ? errors.modules
                      : "Select at least one module"}
                  </p>
                )}
              </div>
              <div className="col-12 py-1"></div>
            </CardComp>
            <div className="d-flex gap-3">
              <ButtonComp
                btnText={roleId ? "Update Role" : "Submit Role"}
                type="submit"
                loading={creating || updating || isSubmitting}
                btnClassName={
                  "bg-primary1 py-3 px-4 text-white border-0 rounded"
                }
              />
              <ButtonComp
                btnText={"Cancel"}
                type="button"
                onClick={() =>
                  navigate("/super_admin/settings/role_management")
                }
                btnClassName={"py-3 px-4 border rounded"}
              />
            </div>
          </Form>
        )}
      </Formik>
    </Wrapper>
  );
}

function ModulePermissionBlock({
  module,
  selected,
  onChange,
  defaultOpen = false,
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [boxHeight, setBoxHeight] = useState("0px");
  const itemsRef = useRef(null);
  const entry = selected.find((m) => m.module_id === module.id);
  const isParentChecked = Boolean(entry);
  const subModules = module.subModules || module.sub_modules || [];

  useEffect(() => {
    if (defaultOpen) {
      setIsOpen(true);
    }
  }, [defaultOpen, module.id]);

  useEffect(() => {
    if (!itemsRef.current) return;
    const itemsHeight = itemsRef.current.getBoundingClientRect().height;
    setBoxHeight(isOpen ? `${itemsHeight}px` : "0px");
  }, [isOpen, subModules.length]);

  const toggleParent = () => {
    if (isParentChecked) {
      onChange(selected.filter((m) => m.module_id !== module.id));
    } else {
      onChange([
        ...selected,
        { module_id: module.id, sub_module_ids: [] },
      ]);
      setIsOpen(true);
    }
  };

  const toggleSub = (subId) => {
    const current = selected.find((m) => m.module_id === module.id);
    if (!current) {
      onChange([
        ...selected,
        { module_id: module.id, sub_module_ids: [subId] },
      ]);
      return;
    }
    const has = current.sub_module_ids.includes(subId);
    const nextSubs = has
      ? current.sub_module_ids.filter((id) => id !== subId)
      : [...current.sub_module_ids, subId];
    onChange(
      selected.map((m) =>
        m.module_id === module.id
          ? { ...m, sub_module_ids: nextSubs }
          : m
      )
    );
  };

  const selectAllSubs = () => {
    const allIds = subModules.map((s) => s.id);
    if (!entry) {
      onChange([
        ...selected,
        { module_id: module.id, sub_module_ids: allIds },
      ]);
    } else {
      onChange(
        selected.map((m) =>
          m.module_id === module.id
            ? { ...m, sub_module_ids: allIds }
            : m
        )
      );
    }
  };

  return (
    <article className="mb-2">
      <div className="permission-header d-flex justify-content-between align-items-center px-3 py-2">
        <label className="d-flex gap-2 align-items-center mb-0">
          <input
            type="checkbox"
            checked={isParentChecked}
            onChange={toggleParent}
          />
          <span className="fw-medium">{module.name}</span>
        </label>
        <button
          type="button"
          className={`border-0 bg-transparent ${isOpen ? "open" : ""}`}
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Toggle sub-modules"
        >
          <RiArrowDropDownLine size={30} />
        </button>
      </div>
      <div className="items-box" style={{ height: boxHeight }}>
        <ul ref={itemsRef} className="items px-3 mb-0">
          {subModules.length > 0 && (
            <li className="mb-2">
              <button
                type="button"
                className="btn btn-link btn-sm p-0"
                onClick={selectAllSubs}
                disabled={!isParentChecked}
              >
                Select all sub-modules
              </button>
            </li>
          )}
          {subModules.map((sub) => (
            <li key={sub.id} className="mb-1">
              <label className="d-flex gap-2 align-items-center mb-0">
                <input
                  type="checkbox"
                  disabled={!isParentChecked}
                  checked={Boolean(
                    entry?.sub_module_ids?.includes(sub.id)
                  )}
                  onChange={() => toggleSub(sub.id)}
                />
                <span>{sub.name}</span>
              </label>
            </li>
          ))}
          {subModules.length === 0 && (
            <li className="text-muted small">No sub-modules</li>
          )}
        </ul>
      </div>
    </article>
  );
}

const Wrapper = styled.div`
  .permissions {
    background-color: #ffffff;
    max-width: 480px;
    max-height: 420px;
    overflow-y: auto;
    padding-right: 4px;
  }
  .permission-search input {
    background-color: #fafafa;
    border: 1px solid #a1a0a0;
    border-radius: 5px;
    font-size: 14px;
    padding: 0.5rem 0.75rem;
  }
  .permission-search input:focus {
    border-color: #0556e5;
    box-shadow: none;
    outline: none;
  }
  ul {
    list-style: none;
  }
  .items-box {
    overflow: hidden;
    transition: all 400ms;
  }
  .items {
    padding-block: 0.5rem;
  }
  .permission-header {
    background-color: #fafafa;
  }
  span.open,
  button.open {
    transform: rotate(180deg);
  }
`;

export default RoleForm;
